import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Button,
  Checkbox,
  Chip,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Dialog as MuiDialog,
  Select,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { Dialog } from "components/Dialog";
import { getUtcDisplayDate } from "encamp-shared/src/utils/date";
import { gql } from "generated-graphql";
import { BulkManifestDiscrepanciesQuery } from "generated-graphql/graphql";
import GET_DOCUMENT_DOWNLOAD_LINK_QUERY from "queries/getDocumentDownloadLink";
import React, { useCallback, useMemo, useState } from "react";
import { useParams } from "react-router-dom";

import { CREATE_EXCEPTION_REPORT_SUBMISSION, PROCESSED_MANIFESTS_QUERY } from "./api";
import { ProofOfContactDialog } from "./ProofOfContactDialog";

// --- GraphQL ---

export const BULK_MANIFEST_DISCREPANCIES_QUERY = gql(`
  query BulkManifestDiscrepancies($processedManifestIds: [ID!]!) {
    bulkManifestDiscrepancies(processedManifestIds: $processedManifestIds) {
      id
      processedManifestId
      manifestTrackingNumber
      facilityName
      epaId
      wasteLineUnifiedId
      wasteLineNumber
      field
      isDot
      severity
      status
      eManifestValue
      vendorValue
      manifestDocumentValue
      sourceDocumentId
      sourceDocumentFileExtension
      escalation
      resolvedAt
      resolvedBy {
        id
        person {
          first
          last
        }
      }
      resolutionType
      resolutionNote
      createdAt
    }
  }
`);

const UPDATE_DISCREPANCY_VENDOR_DATA = gql(`
  mutation UpdateDiscrepancyVendorData($discrepancyIds: [ID!]!) {
    updateDiscrepancyVendorData(discrepancyIds: $discrepancyIds) {
      id
      processedManifestId
      field
      status
      eManifestValue
      vendorValue
      resolvedAt
      resolvedBy {
        id
        person {
          first
          last
        }
      }
      resolutionType
      resolutionNote
    }
  }
`);

const RESOLVE_DISCREPANCIES_EXTERNALLY = gql(`
  mutation ResolveDiscrepanciesExternally($discrepancyIds: [ID!]!) {
    resolveDiscrepanciesExternally(discrepancyIds: $discrepancyIds) {
      id
      processedManifestId
      field
      status
      resolvedAt
      resolvedBy {
        id
        person {
          first
          last
        }
      }
      resolutionType
      resolutionNote
    }
  }
`);

const DISMISS_DISCREPANCIES = gql(`
  mutation DismissDiscrepancies($discrepancyIds: [ID!]!, $note: String) {
    dismissDiscrepancies(discrepancyIds: $discrepancyIds, note: $note) {
      id
      processedManifestId
      field
      status
      resolvedAt
      resolvedBy {
        id
        person {
          first
          last
        }
      }
      resolutionType
      resolutionNote
    }
  }
`);

export const COMPLIANCE_MANIFEST_DATA_QUERY = gql(`
  query ComplianceManifestData($search: String, $page: Int, $pageSize: Int) {
    processedManifests(search: $search, page: $page, pageSize: $pageSize) {
      items {
        id
        shippedDate
        transporterContactedAt
        transporterContactedBy
        exceptionReportSubmission {
          id
          title
          status
        }
      }
    }
  }
`);

/** A discrepancy is a compliance row if it has an escalation level. */
function isComplianceDiscrepancy(d: { escalation?: string | null }): boolean {
  return d.escalation != null;
}

/** Escalation levels that require user action. */
const ACTIONABLE_ESCALATIONS = new Set(["OVERDUE_45", "OVERDUE_60"]);

/**
 * Fields where "Update Vendor Data" is not supported.
 * Matches BLOCKED_FIELDS in the resolver.
 */
const UPDATE_VENDOR_DATA_BLOCKED_FIELDS = new Set([
  "eManifestMissing",
  "wasteLinesCount",
  "shippedDate",
  "destinationFacilityId",
]);

// --- Per-action eligibility ---
//
// Each bulk action has backend constraints on which discrepancies it can
// apply to. We mirror those constraints client-side so users see the
// breakdown up front and only eligible IDs are sent to the mutation.
// Source of truth for the rules:
//   - resolveDiscrepancies / dismissDiscrepancies / transitionToPending in
//     packages/encamp-waste-data-access/src/discrepancies/discrepancyWriter.ts
//   - BLOCKED_FIELDS and compliance check in
//     apps/api/src/resolvers/waste/processedManifestCompliance.ts

type ActionKey = "updateVendorData" | "resolveExternally" | "nonDiscrepancy";

type SkipGroup = {
  reason: string;
  count: number;
};

type ActionEligibility = {
  eligibleIds: string[];
  skips: SkipGroup[];
};

function computeActionEligibility(
  action: ActionKey,
  selected: readonly Discrepancy[]
): ActionEligibility {
  const eligibleIds: string[] = [];
  const skipCounts = new Map<string, number>();
  const addSkip = (reason: string) => {
    skipCounts.set(reason, (skipCounts.get(reason) ?? 0) + 1);
  };

  for (const d of selected) {
    if (action === "updateVendorData") {
      if (d.status !== "UNRESOLVED") {
        addSkip("already resolved or pending");
      } else if (UPDATE_VENDOR_DATA_BLOCKED_FIELDS.has(d.field)) {
        addSkip(`${getFieldLabel(d.field)} doesn't support vendor data update`);
      } else {
        eligibleIds.push(d.id);
      }
    } else if (action === "resolveExternally") {
      if (d.status === "RESOLVED") {
        addSkip("already resolved");
      } else if (d.status === "PENDING") {
        addSkip("already pending");
      } else {
        eligibleIds.push(d.id);
      }
    } else {
      // nonDiscrepancy
      if (d.status === "RESOLVED" || d.status === "IGNORED") {
        addSkip("already resolved");
      } else if (isComplianceDiscrepancy(d)) {
        addSkip("compliance row — use Resolve Externally instead");
      } else {
        eligibleIds.push(d.id);
      }
    }
  }

  const skips = [...skipCounts.entries()]
    .map(([reason, count]) => ({ reason, count }))
    .sort((a, b) => b.count - a.count);

  return { eligibleIds, skips };
}

/** Render skip summary for tooltip or dialog description. */
function formatSkipSummary(skips: SkipGroup[]): string {
  return skips.map((s) => `• ${s.count} ${s.reason}`).join("\n");
}

// --- Types ---

type Discrepancy = BulkManifestDiscrepanciesQuery["bulkManifestDiscrepancies"][number];

type DiscrepancyStatus = "unresolved" | "pending" | "resolved";

// --- Field label map ---

const FIELD_LABELS: Record<string, string> = {
  generatorName: "Generator Name",
  generatorId: "Generator EPA ID",
  generatorStreetAddress: "Generator Street Address",
  generatorStreet2: "Generator Street 2",
  generatorCity: "Generator City",
  generatorState: "Generator State",
  generatorZip: "Generator ZIP",
  shippedDate: "Ship Date",
  destinationFacilityId: "TSDF EPA ID",
  destinationFacilityName: "TSDF Name",
  wasteLinesCount: "Waste Line Count",
  quantity: "Quantity",
  quantityUnitOfMeasureCode: "UOM Code",
  quantityUnitOfMeasureDescription: "UOM Description",
  containerTypeCode: "Container Type",
  managementMethodCode: "Management Method",
  federalWasteCodes: "Federal Waste Codes",
  stateWasteCodes: "State Waste Codes",
  dotPrintedInformation: "DOT Description",
  nonHazWasteDescription: "Non-Haz Description",
  eManifestMissing: "Missing e-Manifest",
};

function getFieldLabel(field: string): string {
  return FIELD_LABELS[field] ?? field;
}

// --- Helpers ---

function getDiscrepancyStatus(d: Discrepancy): DiscrepancyStatus {
  switch (d.status) {
    case "RESOLVED":
    case "IGNORED":
      return "resolved";
    case "PENDING":
      return "pending";
    case "UNRESOLVED":
    default:
      return "unresolved";
  }
}

function getStatusColor(status: DiscrepancyStatus) {
  switch (status) {
    case "unresolved":
      return "warning" as const;
    case "pending":
      return "info" as const;
    case "resolved":
      return "success" as const;
  }
}

function getStatusLabel(status: DiscrepancyStatus) {
  switch (status) {
    case "unresolved":
      return "Unresolved";
    case "pending":
      return "Pending";
    case "resolved":
      return "Resolved";
  }
}

function SectionHeaderRow({
  label,
  count,
  colSpan,
  collapsed,
  onToggle,
}: {
  label: string;
  count: number;
  colSpan: number;
  collapsed: boolean;
  onToggle: () => void;
}) {
  const theme = useTheme();
  return (
    <TableRow
      onClick={onToggle}
      sx={{
        cursor: "pointer",
        "&:hover": { bgcolor: theme.palette.grey[200] },
      }}
    >
      <TableCell
        colSpan={colSpan}
        sx={{
          bgcolor: theme.palette.grey[100],
          py: 0.75,
          px: 2,
          fontWeight: 600,
          fontSize: "0.8125rem",
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={0.5}>
          {collapsed ? (
            <ExpandMoreIcon sx={{ fontSize: 18 }} />
          ) : (
            <ExpandLessIcon sx={{ fontSize: 18 }} />
          )}
          <span>{label}</span>
          <Chip size="small" label={count} sx={{ height: 20, fontSize: "0.75rem" }} />
        </Stack>
      </TableCell>
    </TableRow>
  );
}

// --- Bulk action button ---

/**
 * What's stored in state when the user clicks a bulk action button. Kept as
 * plain data (not callbacks) so the Confirm click handler can read current
 * state at confirm-time — including the optional note the user types *after*
 * opening the dialog. Storing a callback here would freeze closure vars like
 * `confirmNote` at click-time and silently drop notes.
 */
type ConfirmActionState = {
  label: string;
  description: string;
  action: ActionKey;
  eligibleIds: string[];
};

interface BulkActionButtonProps {
  label: string;
  /** Plain-language description of what the action does to eligible rows. */
  baseDescription: string;
  /** Pre-computed eligibility derived from the current selection. */
  eligibility: ActionEligibility;
  /** Total number of rows the user has currently selected. */
  totalSelected: number;
  /** Disable while any mutation is in flight. */
  loading: boolean;
  /** Which action this button represents — dispatched at confirm-time. */
  action: ActionKey;
  setConfirmAction: (action: ConfirmActionState | null) => void;
}

/**
 * Bulk action button with per-action eligibility awareness.
 *
 * - Disabled when the user has selected nothing OR when none of the selected
 *   rows are eligible for this action.
 * - Tooltip summarizes the eligibility split (e.g. "3 will be resolved, 2
 *   skipped: field doesn't support vendor data update").
 * - Confirmation dialog description restates the split so the user sees
 *   exactly which rows will run and which will be skipped before committing.
 * - Only eligible IDs reach state (and therefore the mutation) — skipped
 *   rows never hit the backend, so a single "bad" row can't roll back the
 *   whole batch.
 */
function BulkActionButton({
  label,
  baseDescription,
  eligibility,
  totalSelected,
  loading,
  action,
  setConfirmAction,
}: BulkActionButtonProps) {
  const { eligibleIds, skips } = eligibility;
  const eligibleCount = eligibleIds.length;
  const skipCount = totalSelected - eligibleCount;
  const nothingSelected = totalSelected === 0;
  const nothingEligible = totalSelected > 0 && eligibleCount === 0;

  let tooltipTitle = "";
  if (nothingSelected) {
    tooltipTitle = "";
  } else if (nothingEligible) {
    tooltipTitle = `None of the ${totalSelected} selected rows support ${label}.\n${formatSkipSummary(skips)}`;
  } else if (skipCount > 0) {
    tooltipTitle = `${eligibleCount} of ${totalSelected} will run. ${skipCount} will be skipped:\n${formatSkipSummary(skips)}`;
  }

  const rowWord = (n: number) => (n === 1 ? "row" : "rows");
  const description =
    skipCount > 0
      ? `${baseDescription}\n\n${eligibleCount} of ${totalSelected} selected ${rowWord(totalSelected)} will be processed. ${skipCount} will be skipped:\n${formatSkipSummary(skips)}`
      : `${baseDescription}\n\nThis will apply to ${eligibleCount} selected ${rowWord(eligibleCount)}.`;

  return (
    <Tooltip
      title={tooltipTitle}
      placement="top"
      enterDelay={300}
      slotProps={{ tooltip: { sx: { whiteSpace: "pre-line" } } }}
    >
      <span>
        <Button
          variant="outlined"
          size="small"
          color="warning"
          disabled={nothingSelected || nothingEligible || loading}
          onClick={() =>
            setConfirmAction({
              label,
              description,
              action,
              eligibleIds,
            })
          }
        >
          {label}
        </Button>
      </span>
    </Tooltip>
  );
}

// --- Main component ---

interface DiscrepancyReviewDialogProps {
  processedManifestIds: string[];
  /** Optional manifest tracking number for single-manifest title display */
  manifestTrackingNumber?: string;
  /** When provided, only show discrepancies for these waste line IDs (line-item mode) */
  wasteLineUnifiedIds?: string[];
  onClose: () => void;
}

export function DiscrepancyReviewDialog({
  processedManifestIds,
  manifestTrackingNumber,
  wasteLineUnifiedIds,
  onClose,
}: DiscrepancyReviewDialogProps) {
  const { tenantId } = useParams<{ tenantId: string }>();
  const theme = useTheme();
  const alerts = useAlerts();
  const [statusFilter, setStatusFilter] = useState<DiscrepancyStatus[]>(["unresolved", "pending"]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const [getDownloadLink] = useLazyQuery(GET_DOCUMENT_DOWNLOAD_LINK_QUERY, {
    fetchPolicy: "network-only",
  });

  const openDocumentInNewTab = useCallback(
    async (documentId: string) => {
      try {
        const { data } = await getDownloadLink({ variables: { id: documentId } });
        const url = data?.getDocumentDownloadLink;
        if (url) {
          window.open(url, "_blank");
        }
      } catch {
        alerts.error("Failed to load document");
      }
    },
    [getDownloadLink, alerts]
  );

  // Use operation name strings so Apollo refetches all active instances
  // regardless of variable differences (list vs drawer vs dialog).
  const parentRefetchQueries = ["ProcessedManifests", "ComplianceManifestData"];

  const [updateVendorData, { loading: updatingVendorData }] = useMutation(
    UPDATE_DISCREPANCY_VENDOR_DATA,
    { refetchQueries: parentRefetchQueries }
  );

  const [resolveExternally, { loading: resolvingExternally }] = useMutation(
    RESOLVE_DISCREPANCIES_EXTERNALLY,
    { refetchQueries: parentRefetchQueries }
  );

  const [dismiss, { loading: dismissing }] = useMutation(DISMISS_DISCREPANCIES, {
    // Also refetch the discrepancy list since IGNORED items are filtered
    // server-side and Apollo's cache merge won't remove them from the array.
    refetchQueries: [...parentRefetchQueries, "BulkManifestDiscrepancies"],
  });

  const mutationLoading = updatingVendorData || resolvingExternally || dismissing;

  const [confirmAction, setConfirmAction] = useState<ConfirmActionState | null>(null);
  const [confirmNote, setConfirmNote] = useState("");

  // Dispatch the confirmed bulk action. Reads confirmAction + confirmNote from
  // current state at invocation-time — the TextField for the note lives inside
  // the confirmation dialog, so the user types the note AFTER opening it.
  // Pre-binding the action to a callback at click-time (like we used to) would
  // freeze confirmNote to its click-time value and silently drop the note.
  const handleConfirm = useCallback(async () => {
    if (!confirmAction) return;
    const { action, eligibleIds, label } = confirmAction;
    const note = confirmNote || null;
    try {
      if (action === "updateVendorData") {
        await updateVendorData({ variables: { discrepancyIds: eligibleIds } });
        alerts.success(`${eligibleIds.length} discrepancies resolved — vendor data updated`);
      } else if (action === "resolveExternally") {
        await resolveExternally({ variables: { discrepancyIds: eligibleIds } });
        alerts.success(`${eligibleIds.length} discrepancies marked as pending external resolution`);
      } else {
        await dismiss({ variables: { discrepancyIds: eligibleIds, note } });
        alerts.success(`${eligibleIds.length} items dismissed as non-discrepancies`);
      }
      setSelectedIds(new Set());
    } catch (err) {
      alerts.error(`Failed to ${label.toLowerCase()}`, err instanceof Error ? err : undefined);
    }
    setConfirmAction(null);
    setConfirmNote("");
  }, [confirmAction, confirmNote, updateVendorData, resolveExternally, dismiss, alerts]);

  const { data, loading } = useQuery(BULK_MANIFEST_DISCREPANCIES_QUERY, {
    variables: { processedManifestIds },
  });

  // Fetch compliance manifest data for exception report / proof-of-contact actions
  const compliancePmIds = useMemo(() => {
    const raw = data?.bulkManifestDiscrepancies ?? [];
    const ids = new Set<string>();
    for (const d of raw) {
      if (isComplianceDiscrepancy(d)) {
        ids.add(d.processedManifestId);
      }
    }
    return [...ids];
  }, [data?.bulkManifestDiscrepancies]);

  const { data: complianceData } = useQuery(COMPLIANCE_MANIFEST_DATA_QUERY, {
    variables: {
      search: `tenantId:${tenantId} id:${compliancePmIds.join("⁋")}`,
      page: 0,
      pageSize: compliancePmIds.length,
    },
    skip: compliancePmIds.length === 0 || !tenantId,
  });

  const complianceManifestMap = useMemo(() => {
    const map = new Map<
      string,
      NonNullable<typeof complianceData>["processedManifests"]["items"][number]
    >();
    for (const item of complianceData?.processedManifests?.items ?? []) {
      map.set(item.id, item);
    }
    return map;
  }, [complianceData]);

  const [createExceptionReport, { loading: creatingExceptionReport }] = useMutation(
    CREATE_EXCEPTION_REPORT_SUBMISSION,
    {
      refetchQueries: [
        PROCESSED_MANIFESTS_QUERY,
        BULK_MANIFEST_DISCREPANCIES_QUERY,
        COMPLIANCE_MANIFEST_DATA_QUERY,
      ],
    }
  );

  const [showProofOfContactForPmId, setShowProofOfContactForPmId] = useState<string | null>(null);

  const allDiscrepancies = useMemo(() => {
    const raw = data?.bulkManifestDiscrepancies ?? [];
    if (!wasteLineUnifiedIds) return raw;
    const wluSet = new Set(wasteLineUnifiedIds);
    return raw.filter((d) => d.wasteLineUnifiedId && wluSet.has(d.wasteLineUnifiedId));
  }, [data?.bulkManifestDiscrepancies, wasteLineUnifiedIds]);

  const isLineItemMode = Boolean(wasteLineUnifiedIds);
  const isSingleManifest = processedManifestIds.length === 1;

  let title: string;
  if (isLineItemMode) {
    title = `Line Item Discrepancies — ${wasteLineUnifiedIds?.length ?? 0} line items`;
  } else if (isSingleManifest) {
    title = `Manifest Discrepancies — #${manifestTrackingNumber ?? processedManifestIds[0].slice(0, 8)}`;
  } else {
    title = `Bulk Discrepancy Review — ${processedManifestIds.length} manifests`;
  }

  // Apply status filter
  const discrepancies = useMemo(() => {
    if (statusFilter.length === 0) return allDiscrepancies;
    return allDiscrepancies.filter((d) => statusFilter.includes(getDiscrepancyStatus(d)));
  }, [allDiscrepancies, statusFilter]);

  // Group by field name
  const fieldGroups = useMemo(() => {
    const groups = new Map<string, Discrepancy[]>();
    for (const d of discrepancies) {
      const existing = groups.get(d.field);
      if (existing) {
        existing.push(d);
      } else {
        groups.set(d.field, [d]);
      }
    }
    return [...groups.entries()].map(([field, items]) => ({
      field,
      label: getFieldLabel(field),
      items,
    }));
  }, [discrepancies]);

  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());
  const toggleGroup = (field: string) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(field)) {
        next.delete(field);
      } else {
        next.add(field);
      }
      return next;
    });
  };

  // Selection
  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const allSelected = discrepancies.length > 0 && discrepancies.every((d) => selectedIds.has(d.id));

  const someSelected = selectedIds.size > 0;

  const selectedDiscrepancies = useMemo(
    () => discrepancies.filter((d) => selectedIds.has(d.id)),
    [discrepancies, selectedIds]
  );

  const updateVendorDataEligibility = useMemo(
    () => computeActionEligibility("updateVendorData", selectedDiscrepancies),
    [selectedDiscrepancies]
  );
  const resolveExternallyEligibility = useMemo(
    () => computeActionEligibility("resolveExternally", selectedDiscrepancies),
    [selectedDiscrepancies]
  );
  const nonDiscrepancyEligibility = useMemo(
    () => computeActionEligibility("nonDiscrepancy", selectedDiscrepancies),
    [selectedDiscrepancies]
  );

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(discrepancies.map((d) => d.id)));
    }
  };

  const COL_COUNT = 7; // checkbox, facility, manifest, eManifest, vendor, document, status

  const formatDateOnly = (value: string | null | undefined): string => {
    if (value == null) return "—";
    // Extract just the date portion (YYYY-MM-DD) from ISO strings
    const match = value.match(/^(\d{4}-\d{2}-\d{2})/);
    return match ? match[1] : value;
  };

  const formatDisplayValue = (value: string | null | undefined): string => {
    if (value == null) return "—";
    // Empty array representation
    if (value === "[]") return "—";
    // Strip surrounding quotes from individual values and array items
    const stripped = value.replace(/^"(.*)"$/, "$1").replace(/"\s*,\s*"/g, ", ");
    // Strip quotes from bracket-wrapped lists like ["D001", "D002"]
    const unbracketed = stripped.replace(/^\[(.*)\]$/, "$1").replace(/"/g, "");
    return unbracketed || "—";
  };

  const renderValueCell = (
    value: string | null | undefined,
    discrepancy: Discrepancy,
    source: "eManifest" | "vendor"
  ) => {
    const display =
      discrepancy.field === "shippedDate" ? formatDateOnly(value) : formatDisplayValue(value);
    return (
      <TableCell
        sx={{
          maxWidth: 240,
          wordBreak: "break-word",
        }}
      >
        <Typography variant="body2">{display}</Typography>
      </TableCell>
    );
  };

  const renderComplianceActions = (d: Discrepancy) => {
    const pm = complianceManifestMap.get(d.processedManifestId);

    if (d.escalation === "OVERDUE_60") {
      if (pm?.exceptionReportSubmission) {
        return (
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="body2">{pm.exceptionReportSubmission.title}</Typography>
            <Button
              size="small"
              variant="text"
              href={`/o/${tenantId}/waste/submissions/${pm.exceptionReportSubmission.id}`}
            >
              View
            </Button>
          </Stack>
        );
      }
      return (
        <Button
          size="small"
          variant="outlined"
          color="error"
          disabled={creatingExceptionReport}
          onClick={async () => {
            try {
              await createExceptionReport({
                variables: { processedManifestId: d.processedManifestId },
              });
              alerts.success("Exception report submission created");
            } catch {
              alerts.error("Failed to create exception report submission");
            }
          }}
        >
          Create Exception Report
        </Button>
      );
    }

    if (d.escalation === "OVERDUE_45") {
      if (pm?.transporterContactedAt) {
        return (
          <Typography variant="body2">
            Contacted {pm.transporterContactedBy ? `by ${pm.transporterContactedBy}` : ""} on{" "}
            {getUtcDisplayDate(pm.transporterContactedAt, "med")}
          </Typography>
        );
      }
      return (
        <Button
          size="small"
          variant="outlined"
          onClick={() => setShowProofOfContactForPmId(d.processedManifestId)}
        >
          Add Proof of Contact
        </Button>
      );
    }

    return null;
  };

  const renderDiscrepancyRow = (d: Discrepancy) => {
    const status = getDiscrepancyStatus(d);
    const isSelected = selectedIds.has(d.id);
    const isComplianceRow = isComplianceDiscrepancy(d);

    return (
      <TableRow key={d.id} hover selected={isSelected}>
        {/* Checkbox */}
        <TableCell padding="checkbox">
          <Checkbox size="small" checked={isSelected} onChange={() => toggleSelect(d.id)} />
        </TableCell>

        {/* Facility */}
        <TableCell sx={{ whiteSpace: "nowrap" }}>
          <Typography variant="body2">{d.facilityName ?? "—"}</Typography>
          <Typography variant="caption" color="text.secondary">
            {d.epaId}
          </Typography>
        </TableCell>

        {/* Manifest tracking number */}
        <TableCell sx={{ whiteSpace: "nowrap" }}>
          <Typography variant="body2" fontWeight={500}>
            {d.manifestTrackingNumber ?? d.processedManifestId.slice(0, 8)}
          </Typography>
          {d.wasteLineUnifiedId && (
            <Typography variant="caption" color="text.secondary">
              Line {d.wasteLineNumber ?? "item"}
            </Typography>
          )}
        </TableCell>

        {isComplianceRow ? (
          <>
            {/* Days elapsed */}
            <TableCell>
              <Typography variant="body2" color="error">
                {(() => {
                  const pm = complianceManifestMap.get(d.processedManifestId);
                  if (!pm?.shippedDate) return "—";
                  const days = Math.floor(
                    (Date.now() - new Date(pm.shippedDate).getTime()) / (1000 * 60 * 60 * 24)
                  );
                  return `${days} days`;
                })()}
              </Typography>
            </TableCell>

            {/* Action / status */}
            <TableCell colSpan={2}>{renderComplianceActions(d)}</TableCell>
          </>
        ) : (
          <>
            {/* eManifest value */}
            {renderValueCell(d.eManifestValue, d, "eManifest")}

            {/* Vendor value */}
            {renderValueCell(d.vendorValue, d, "vendor")}

            {/* Document value */}
            <TableCell sx={{ maxWidth: 240, wordBreak: "break-word" }}>
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <Typography variant="body2">
                  {d.field === "shippedDate"
                    ? formatDateOnly(d.manifestDocumentValue)
                    : formatDisplayValue(d.manifestDocumentValue)}
                </Typography>
                {d.sourceDocumentId && (
                  <Tooltip title="Open source document in new tab">
                    <IconButton
                      size="small"
                      onClick={() => {
                        if (d.sourceDocumentId) {
                          openDocumentInNewTab(d.sourceDocumentId);
                        }
                      }}
                    >
                      <DescriptionOutlinedIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                )}
              </Stack>
            </TableCell>
          </>
        )}

        {/* Status */}
        <TableCell>
          <Chip
            size="small"
            label={getStatusLabel(status)}
            color={getStatusColor(status)}
            variant="outlined"
          />
        </TableCell>
      </TableRow>
    );
  };

  return (
    <>
      <Dialog
        open
        onClose={onClose}
        maxWidth="xl"
        fullWidth
        slotProps={{ paper: { sx: { maxHeight: "90vh" } } }}
        title={title}
        noPrimaryAction
        titleBarActionElement={
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Status</InputLabel>
            <Select
              multiple
              value={statusFilter}
              label="Status"
              onChange={(e) => {
                const value = e.target.value;
                setStatusFilter(
                  typeof value === "string"
                    ? (value.split(",") as DiscrepancyStatus[])
                    : (value as DiscrepancyStatus[])
                );
              }}
              renderValue={(selected) => selected.map((s) => getStatusLabel(s)).join(", ")}
            >
              <MenuItem value="unresolved">Unresolved</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="resolved">Resolved</MenuItem>
            </Select>
          </FormControl>
        }
      >
        {loading ? (
          <Stack spacing={2}>
            {/* Skeleton action bar */}
            <Stack direction="row" spacing={1}>
              {Array.from({ length: 4 }, (_, i) => (
                <Skeleton key={i} variant="rounded" width={140} height={32} />
              ))}
            </Stack>
            {/* Skeleton table rows */}
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Skeleton variant="rectangular" width={18} height={18} />
                  </TableCell>
                  {["Facility", "Manifest", "e-Manifest", "Vendor", "Document", "Status"].map(
                    (h) => (
                      <TableCell key={h}>
                        <Skeleton variant="text" width={80} />
                      </TableCell>
                    )
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {Array.from({ length: 8 }, (_, i) => (
                  <TableRow key={i}>
                    <TableCell padding="checkbox">
                      <Skeleton variant="rectangular" width={18} height={18} />
                    </TableCell>
                    <TableCell>
                      <Skeleton variant="text" width={120} />
                      <Skeleton variant="text" width={80} />
                    </TableCell>
                    <TableCell>
                      <Skeleton variant="text" width={100} />
                    </TableCell>
                    <TableCell>
                      <Skeleton variant="text" width={140} />
                    </TableCell>
                    <TableCell>
                      <Skeleton variant="text" width={140} />
                    </TableCell>
                    <TableCell>
                      <Skeleton variant="text" width={60} />
                    </TableCell>
                    <TableCell>
                      <Skeleton variant="rounded" width={80} height={24} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Stack>
        ) : (
          <>
            {/* Bulk action bar */}
            <Stack
              direction="row"
              spacing={1}
              sx={{
                mb: 2,
                position: "sticky",
                top: 0,
                zIndex: 3,
                bgcolor: "background.paper",
                py: 1,
              }}
            >
              <BulkActionButton
                label="Update Vendor Data"
                baseDescription="Update vendor data to match e-Manifest values, marking them as resolved."
                eligibility={updateVendorDataEligibility}
                totalSelected={selectedIds.size}
                loading={mutationLoading}
                action="updateVendorData"
                setConfirmAction={setConfirmAction}
              />
              <BulkActionButton
                label="Resolve Externally"
                baseDescription={
                  'Mark as pending external resolution (e.g. updating the e-Manifest value in RCRAInfo). These will move to "Pending" until the change propagates back through the system.'
                }
                eligibility={resolveExternallyEligibility}
                totalSelected={selectedIds.size}
                loading={mutationLoading}
                action="resolveExternally"
                setConfirmAction={setConfirmAction}
              />
              <BulkActionButton
                label="Non-Discrepancy"
                baseDescription="Mark as non-discrepancies. The difference is expected and will be hidden from active review."
                eligibility={nonDiscrepancyEligibility}
                totalSelected={selectedIds.size}
                loading={mutationLoading}
                action="nonDiscrepancy"
                setConfirmAction={setConfirmAction}
              />
            </Stack>

            {/* Comparison table */}
            <TableContainer>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        size="small"
                        checked={allSelected}
                        indeterminate={someSelected && !allSelected}
                        onChange={toggleSelectAll}
                      />
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, minWidth: 160 }}>Facility</TableCell>
                    <TableCell sx={{ fontWeight: 600, minWidth: 140 }}>Manifest</TableCell>
                    <TableCell sx={{ fontWeight: 600, minWidth: 200 }}>e-Manifest</TableCell>
                    <TableCell sx={{ fontWeight: 600, minWidth: 200 }}>Vendor</TableCell>
                    <TableCell sx={{ fontWeight: 600, minWidth: 140 }}>Document</TableCell>
                    <TableCell sx={{ fontWeight: 600, minWidth: 100 }}>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {fieldGroups.map((group) => (
                    <React.Fragment key={group.field}>
                      <SectionHeaderRow
                        label={group.label}
                        count={group.items.length}
                        colSpan={COL_COUNT}
                        collapsed={collapsedGroups.has(group.field)}
                        onToggle={() => toggleGroup(group.field)}
                      />
                      {!collapsedGroups.has(group.field) && group.items.map(renderDiscrepancyRow)}
                    </React.Fragment>
                  ))}

                  {discrepancies.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={COL_COUNT} align="center" sx={{ py: 4 }}>
                        <Typography variant="body2" color="text.secondary">
                          {(() => {
                            if (statusFilter.length > 0) {
                              return "No discrepancies found matching selected status";
                            }
                            if (isSingleManifest) {
                              return "No discrepancies found for this manifest";
                            }
                            return "No discrepancies found for the selected manifests";
                          })()}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </Dialog>

      {showProofOfContactForPmId && (
        <ProofOfContactDialog
          open
          onClose={() => setShowProofOfContactForPmId(null)}
          processedManifestId={showProofOfContactForPmId}
        />
      )}

      {/* Confirmation dialog */}
      <MuiDialog
        open={confirmAction !== null}
        onClose={() => {
          setConfirmAction(null);
          setConfirmNote("");
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{confirmAction?.label}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2, whiteSpace: "pre-line" }}>
            {confirmAction?.description}
          </DialogContentText>
          <TextField
            label="Note (optional)"
            multiline
            minRows={2}
            maxRows={4}
            fullWidth
            size="small"
            value={confirmNote}
            onChange={(e) => setConfirmNote(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setConfirmAction(null);
              setConfirmNote("");
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="warning"
            disabled={mutationLoading}
            onClick={handleConfirm}
          >
            {confirmAction?.label}
          </Button>
        </DialogActions>
      </MuiDialog>
    </>
  );
}
